import { useEffect, useState } from 'react'
import {
  useOperatorBalance,
  useOperatorDeduct,
  useOperatorTopup,
  useOperatorUserTeams,
} from '../api/hooks'
import { createNadDebug } from '../utils/debugLog'
import { useTranslation } from 'react-i18next'
import { useLocaleFormat } from '../i18n/useLocaleFormat'
import AdminToolNav from '../components/admin/AdminToolNav'
import ListboxSelect from '../components/teams/ListboxSelect'

// NAD trace for the operator top-up flow (feature=topup-gate). PII-safe: logs
// only the recipient's email domain, never the full address.
const topupGateLog = createNadDebug('dw:topup-gate:operator-ui')

function emailDomain(email: string): string {
  if (!email || !email.includes('@')) return '<none>'
  return email.split('@').pop()!.toLowerCase()
}

function looksLikeEmail(email: string): boolean {
  const trimmed = email.trim()
  return trimmed.length > 2 && trimmed.includes('@') && !trimmed.endsWith('@')
}

type Direction = 'add' | 'deduct'

interface AdjustResult {
  direction: Direction
  targetEmail: string
  sparks: number
  balanceAfter: number
  alreadyProcessed: boolean
  /** Set when the mint landed in a team pool rather than the recipient's wallet. */
  creditedTeamId: string | null
}

// Admin/operator surface (gated by AdminTopupRoute + the backend broad-admin or
// topup_operator capability). Adds or removes credits FOR a user by email —
// there is no self-serve mint, and no self-serve deduction either.
export default function AdminTopupPage() {
  const topup = useOperatorTopup()
  const deduct = useOperatorDeduct()
  const { t } = useTranslation('pages')
  const { formatNumber } = useLocaleFormat()
  const [email, setEmail] = useState('')
  const [amount, setAmount] = useState('')
  // Optional: credit a shared team's pooled wallet instead of the recipient's
  // own. Blank is the ordinary per-user mint and is unchanged.
  const [teamId, setTeamId] = useState('')
  const [result, setResult] = useState<AdjustResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Confirmation step for deductions only. Adding credits is recoverable by
  // deducting them; taking them away mid-session is not, so it gets a beat.
  const [pendingDeduct, setPendingDeduct] = useState(false)

  // Debounce so the lookup fires once the operator stops typing, not on every
  // keystroke of the address.
  const [lookupEmail, setLookupEmail] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setLookupEmail(email.trim()), 400)
    return () => clearTimeout(handle)
  }, [email])

  const balanceQuery = useOperatorBalance(lookupEmail, looksLikeEmail(lookupEmail))
  const balance = balanceQuery.data

  // The wallets this recipient owns or belongs to. Same debounced email and
  // same enable gate as the balance lookup, so the two panels always describe
  // the same person.
  const teamsQuery = useOperatorUserTeams(lookupEmail, looksLikeEmail(lookupEmail))
  const teamOptions = teamsQuery.data?.teams ?? []

  // The picker's value is a team id, with '' meaning "the recipient's own
  // wallet" — the pre-existing behaviour when the field was blank. Kept as the
  // empty string rather than null so it slots into ListboxSelect's
  // `T extends string` without widening, and so `targetingTeam` below is the
  // same predicate it always was.
  const SELF_WALLET = ''

  // Drop a stale selection when the operator retypes the address. Without this
  // a team id belonging to the PREVIOUS recipient stays staged, and the mint
  // would refuse it as USER_NOT_TEAM_MEMBER — or, far worse, succeed if the new
  // recipient happens to be in that team too, crediting a pool nobody chose.
  useEffect(() => {
    if (teamId === SELF_WALLET) return
    if (!teamOptions.some((team) => team.team_id === teamId)) setTeamId(SELF_WALLET)
    // `teamOptions` is a fresh array each render; keying on the ids keeps this
    // from re-running on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamOptions.map((team) => team.team_id).join(','), teamId])

  const teamLabel = (id: string): string => {
    if (id === SELF_WALLET) return t('adminTopup.teamSelfWallet')
    const team = teamOptions.find((option) => option.team_id === id)
    if (!team) return id
    // NULL balance is "never funded", which is not 0. Saying "0 credits" for a
    // team that has never had a wallet invites an operator to conclude it was
    // spent, and they are about to decide how much to mint.
    const funds =
      team.balance === null
        ? t('adminTopup.teamNeverFunded')
        : t('adminTopup.teamBalance', { balance: formatNumber(team.balance) })
    return `${team.name} · ${funds}`
  }

  const sparks = parseInt(amount, 10)
  const isPending = topup.isPending || deduct.isPending
  const amountValid = !isNaN(sparks) && sparks > 0
  const canSubmit = looksLikeEmail(email) && amountValid && !isPending
  // Deduction has no team equivalent — `deduct_sparks_operator` only ever
  // addresses a user wallet. Leaving the button live with a team id filled in
  // would take credits from the recipient's own balance while the form says
  // "team", so it is blocked rather than quietly retargeted.
  const targetingTeam = teamId.trim().length > 0
  const canDeduct = canSubmit && !targetingTeam

  // There is deliberately NO client-side overdraw check. `deduct_sparks_operator`
  // re-checks the balance under a row lock and returns INSUFFICIENT_CREDITS with
  // the real numbers, so a second check here could only ever be a stale copy of
  // that one — and a stale-low snapshot would block a deduction the server would
  // have accepted. The balance shown above is for the operator to read, not for
  // the page to gate on.

  // Any edit invalidates a shown result / staged confirmation: they describe
  // values that are no longer on screen.
  const resetTransient = () => {
    setResult(null)
    setError(null)
    setPendingDeduct(false)
  }

  const runAdjustment = async (direction: Direction) => {
    if (!canSubmit) return
    setError(null)
    setResult(null)
    setPendingDeduct(false)
    topupGateLog({
      event: direction === 'add' ? 'topup-gate:operator_submit' : 'topup-gate:operator_deduct_submit',
      feature: 'topup-gate',
      concern: 'ui',
      email_domain: emailDomain(email),
      sparks,
    })
    try {
      // The two responses are different shapes — only the mint reports a
      // wallet — so they are handled in separate branches rather than unioned
      // and narrowed, which loses the field's type.
      if (direction === 'add') {
        const res = await topup.mutateAsync({
          targetEmail: email.trim(),
          sparks,
          targetTeamId: targetingTeam ? teamId.trim() : undefined,
        })
        setResult({
          direction,
          targetEmail: res.target_email,
          sparks: res.sparks,
          balanceAfter: res.balance_after,
          alreadyProcessed: res.already_processed,
          // Read back from the response, never from the form: what the wallet
          // layer resolved is the fact worth reporting, and with teams on even
          // a plain user mint reports a personal team.
          creditedTeamId: res.credited_team_id,
        })
      } else {
        const res = await deduct.mutateAsync({ targetEmail: email.trim(), sparks })
        setResult({
          direction,
          targetEmail: res.target_email,
          sparks: res.sparks,
          balanceAfter: res.balance_after,
          alreadyProcessed: res.already_processed,
          creditedTeamId: null,
        })
      }
      setAmount('')
    } catch (err) {
      // Surface the backend reason (the rolling-24h cap rejection, an
      // INSUFFICIENT_CREDITS overdraw, a NO_WALLET target, or an unknown-user
      // 404) to the operator — never silently swallow it.
      console.error(`Operator ${direction} failed:`, err)
      setError(
        err instanceof Error
          ? err.message
          : t(direction === 'add' ? 'adminTopup.topUpFailed' : 'adminTopup.deductFailed'),
      )
    }
  }

  const handleDeductClick = () => {
    if (!canSubmit) return
    if (!pendingDeduct) {
      setPendingDeduct(true)
      return
    }
    // Confirm pressed — always send. An overdraw is the server's call to make.
    void runAdjustment('deduct')
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6" data-testid="admin-topup-page">
      <AdminToolNav />
      <div className="max-w-xl">
        <h1 className="mb-1 text-xl font-semibold text-foreground">{t('adminTopup.title')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">{t('adminTopup.description')}</p>

        <div className="flex flex-col gap-4 rounded-xl border border-border bg-surface-1 p-5">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              {t('adminTopup.targetEmailLabel')}
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                resetTransient()
              }}
              placeholder={t('adminTopup.emailPlaceholder')}
              className="h-9 rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="topup-email-input"
            />
          </label>

          {/* Current balance for the address being typed. Read-only on the
              server — looking someone up never creates or changes a wallet. */}
          <div
            className="rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm"
            data-testid="topup-balance-panel"
          >
            {!looksLikeEmail(lookupEmail) ? (
              <span className="text-muted-foreground">{t('adminTopup.balanceIdle')}</span>
            ) : balanceQuery.isFetching ? (
              <span className="text-muted-foreground" data-testid="topup-balance-loading">
                {t('adminTopup.balanceLoading')}
              </span>
            ) : balanceQuery.isError ? (
              <span className="text-destructive" data-testid="topup-balance-error">
                {balanceQuery.error instanceof Error
                  ? balanceQuery.error.message
                  : t('adminTopup.balanceFailed')}
              </span>
            ) : balance ? (
              <span className="text-foreground" data-testid="topup-balance-value">
                {t(
                  balance.wallet_initialized
                    ? 'adminTopup.balanceCurrent'
                    : 'adminTopup.balanceNoWallet',
                  {
                    email: balance.target_email,
                    balance: formatNumber(balance.balance),
                  },
                )}
              </span>
            ) : null}
          </div>

          {/* Which wallet to credit. Placed after the balance panel because
              that panel reports the RECIPIENT's own wallet, which is not where
              the credits land once a team is picked.

              A picker rather than the free-text UUID field this replaces: an
              operator had no way to discover a team id, and a mistyped one was
              a 404 after the fact. Choosing from the recipient's actual
              memberships also makes USER_NOT_TEAM_MEMBER unreachable by
              construction rather than by care.

              ListboxSelect, never a native <select> — macOS paints its own
              opaque menu regardless of `appearance-none`, which reads as a
              browser artifact rather than part of the product. */}
          <div className="flex flex-col gap-1.5" data-testid="topup-team-picker">
            <span className="text-xs font-medium text-muted-foreground">
              {t('adminTopup.teamPickerLabel')}
            </span>
            {!looksLikeEmail(lookupEmail) ? (
              <span className="text-xs font-medium text-muted-foreground" data-testid="topup-team-idle">
                {t('adminTopup.teamPickerIdle')}
              </span>
            ) : teamsQuery.isFetching ? (
              <span className="text-xs font-medium text-muted-foreground" data-testid="topup-team-loading">
                {t('adminTopup.teamPickerLoading')}
              </span>
            ) : teamsQuery.isError ? (
              <span className="text-xs font-medium text-destructive" data-testid="topup-team-error">
                {teamsQuery.error instanceof Error
                  ? teamsQuery.error.message
                  : t('adminTopup.teamPickerFailed')}
              </span>
            ) : teamsQuery.data && !teamsQuery.data.teams_enabled ? (
              // Reported by the server rather than inferred from an empty list:
              // "teams is off for this account" and "this account is in no
              // team" need different corrections from the operator, and with
              // teams off the mint refuses every team id with TEAMS_DISABLED.
              <span className="text-xs font-medium text-muted-foreground" data-testid="topup-team-disabled">
                {t('adminTopup.teamPickerTeamsOff')}
              </span>
            ) : teamOptions.length === 0 ? (
              <span className="text-xs font-medium text-muted-foreground" data-testid="topup-team-empty">
                {t('adminTopup.teamPickerNoTeams')}
              </span>
            ) : (
              <ListboxSelect
                ariaLabel={t('adminTopup.teamPickerLabel')}
                value={teamId}
                options={[SELF_WALLET, ...teamOptions.map((team) => team.team_id)]}
                labelFor={teamLabel}
                onSelect={(next) => {
                  setTeamId(next)
                  resetTransient()
                }}
                triggerTestId="topup-team-trigger"
                menuTestId="topup-team-menu"
                optionTestId={(id) => `topup-team-option-${id === SELF_WALLET ? 'self' : id}`}
                triggerClassName="h-9 w-full justify-between text-sm"
              />
            )}
            <span className="text-xs font-medium text-muted-foreground">{t('adminTopup.teamPickerHint')}</span>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              {t('adminTopup.amountLabel')}
            </span>
            <input
              type="number"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value)
                resetTransient()
              }}
              placeholder="5000"
              min="1"
              className="h-9 rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="topup-amount-input"
            />
          </label>

          {/* Two explicit actions rather than a signed amount: a stray minus
              sign must never silently invert the operation. */}
          <div className="flex gap-2">
            <button
              onClick={() => void runAdjustment('add')}
              disabled={!canSubmit}
              className="h-9 flex-1 cursor-pointer rounded-lg bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="topup-submit-button"
            >
              {topup.isPending ? t('adminTopup.toppingUp') : t('adminTopup.topUp')}
            </button>
            <button
              onClick={handleDeductClick}
              disabled={!canDeduct}
              className="h-9 flex-1 cursor-pointer rounded-lg border border-destructive/50 bg-transparent px-4 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="deduct-submit-button"
            >
              {deduct.isPending
                ? t('adminTopup.deducting')
                : pendingDeduct
                  ? t('adminTopup.deductConfirm')
                  : t('adminTopup.deduct')}
            </button>
          </div>

          {targetingTeam && (
            <p className="text-sm text-muted-foreground" data-testid="topup-team-deduct-blocked">
              {t('adminTopup.deductTeamUnsupported')}
            </p>
          )}

          {pendingDeduct && !deduct.isPending && (
            <p className="text-sm text-muted-foreground" data-testid="deduct-confirm-prompt">
              {t('adminTopup.deductConfirmPrompt', {
                sparks: formatNumber(sparks),
                email: email.trim(),
              })}
            </p>
          )}

          {result && (
            <div
              className="rounded-lg border border-success/40 bg-success/10 px-3 py-2.5 text-sm text-success"
              data-testid="topup-result"
            >
              {t(
                result.alreadyProcessed
                  ? result.direction === 'add'
                    ? 'adminTopup.resultAlreadyProcessed'
                    : 'adminTopup.resultDeductAlreadyProcessed'
                  : result.direction === 'add'
                    ? 'adminTopup.resultAdded'
                    : 'adminTopup.resultDeducted',
                {
                  sparks: formatNumber(result.sparks),
                  email: result.targetEmail,
                  balance: formatNumber(result.balanceAfter),
                },
              )}
              {/* Named separately rather than folded into the sentence above:
                  "balance" now means the POOL, and an operator reading a team
                  mint as a personal one would top up twice. */}
              {result.direction === 'add' && result.creditedTeamId && (
                <span className="mt-1 block" data-testid="topup-result-team">
                  {t('adminTopup.resultCreditedTeam', {
                    // Name it when we can. The id still appears for a team not
                    // in the fetched list — which is reachable, because the
                    // server reports the wallet it ACTUALLY resolved and that
                    // can be the personal team even on a plain user mint.
                    teamId:
                      teamOptions.find((team) => team.team_id === result.creditedTeamId)?.name ??
                      result.creditedTeamId,
                  })}
                </span>
              )}
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive" data-testid="topup-error">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
